/**
 * Sep 2026 · Global search palette (Ctrl/Cmd+K or the header search icon). Searches pages & features (incl. sub-tabs)
 * locally and the current business's records via GET /api/search — every hit deep-links to the exact page/record.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Search, Loader2, ArrowRight, FileText, Receipt, Users, Landmark, BookOpen, Banknote, Briefcase, Settings, Layers, Wallet, Box, ReceiptText } from 'lucide-react';

export const FEATURES = [
  { title: 'Home dashboard', path: '/dashboard', keywords: 'home overview kpi cash' },
  { title: 'Invoices', path: '/invoices', keywords: 'sales customer receivable' },
  { title: 'Create invoice', path: '/invoices/create', keywords: 'new invoice sale' },
  { title: 'Bills', path: '/bills', keywords: 'purchases vendor payable' },
  { title: 'Create bill', path: '/bills/create', keywords: 'new bill purchase' },
  { title: 'Insights', path: '/insights', keywords: 'analytics charts trends' },
  { title: 'AI CFO', path: '/akountfly-ai', keywords: 'chat assistant analysis' },
  { title: 'Transactions · Bank feeds', path: '/bank-feeds', keywords: 'bank feed transactions statement' },
  { title: 'Transactions · Review needed', path: '/bank-feeds/review', keywords: 'review approve classify' },
  { title: 'Transactions · Reconciliation', path: '/reconciliation', keywords: 'reconcile match bank' },
  { title: 'Bank accounts', path: '/banks', keywords: 'bank connect mono plaid' },
  { title: 'Upload bank statement', path: '/statement-upload', keywords: 'upload pdf csv statement import' },
  { title: 'Reports', path: '/reports', keywords: 'profit loss balance sheet trial balance cash flow' },
  { title: 'Reports · General ledger', path: '/reports/general-ledger', keywords: 'gl ledger report' },
  { title: 'Reports · Sub-ledgers', path: '/reports/sub-ledgers', keywords: 'aged receivables payables' },
  { title: 'Scheduled reports', path: '/scheduled-reports', keywords: 'email schedule report' },
  { title: 'Budgets', path: '/budget', keywords: 'budget vs actuals forecast' },
  { title: 'Products & Contacts · Products', path: '/products-services?tab=products', keywords: 'products services items catalogue' },
  { title: 'Products & Contacts · Contacts', path: '/products-services?tab=contacts', keywords: 'contacts customers vendors suppliers clients' },
  { title: 'More tools', path: '/advanced-view-hub', keywords: 'advanced hub pro tools' },
  { title: 'Chart of accounts (Categories)', path: '/chart-of-accounts', keywords: 'coa gl accounts categories' },
  { title: 'Manual journals (Adjustments)', path: '/manual-journals', keywords: 'journal adjustment entry' },
  { title: 'Recurring journals', path: '/recurring-journals', keywords: 'repeating entries depreciation rent' },
  { title: 'System journals (Auto entries)', path: '/system-journals', keywords: 'auto generated journals' },
  { title: 'General ledger', path: '/general-ledger', keywords: 'all transactions ledger drill down' },
  { title: 'Fixed assets', path: '/fixed-assets', keywords: 'asset depreciation register' },
  { title: 'Loans', path: '/loans', keywords: 'loan schedule interest borrowing' },
  { title: 'Investments', path: '/investments', keywords: 'investment deposit treasury' },
  { title: 'Prepaid expenses', path: '/prepaid-expenses', keywords: 'prepayment amortisation' },
  { title: 'Deferred revenue', path: '/deferred-revenue', keywords: 'unearned revenue recognition' },
  { title: 'Inventory', path: '/inventory', keywords: 'stock closing stock month end' },
  { title: 'FX rates', path: '/fx-rates', keywords: 'currency exchange rate' },
  { title: 'Period close', path: '/period-close', keywords: 'month end lock period' },
  { title: 'Tax compliance', path: '/tax-compliance', keywords: 'vat wht tax filing' },
  { title: 'Tax remittance', path: '/tax-remittance', keywords: 'pay tax remit' },
  { title: 'Annual tax computation', path: '/annual-tax-computation', keywords: 'corporate income tax computation' },
  { title: 'Mileage log', path: '/mileage', keywords: 'mileage trips car travel claim km miles allowance' },
  { title: 'Data import', path: '/import', keywords: 'import csv migration' },
  { title: 'Data export', path: '/data-export', keywords: 'export download backup' },
  { title: 'Opening balances', path: '/opening-balances', keywords: 'opening balance migration start' },
  { title: 'Payroll', path: '/payroll', keywords: 'payroll salaries staff' },
  { title: 'Payroll · People', path: '/payroll/people', keywords: 'employees staff register hire leaver' },
  { title: 'Payroll · Pay runs', path: '/payroll/runs', keywords: 'pay run payslip approve' },
  { title: 'Payroll · Leave balances', path: '/payroll/people#leave', keywords: 'leave holiday annual leave balance accrual carry over payout' },
  { title: 'Payroll · Reports', path: '/payroll/reports', keywords: 'payroll register pension schedule paye' },
  { title: 'Payroll · Year end', path: '/payroll/year-end', keywords: 'p60 w-2 year end certificates' },
  { title: 'Payroll · Notices', path: '/payroll/notices', keywords: 'tax code notices' },
  { title: 'Payroll settings · Pay setup', path: '/payroll/settings?tab=pay-setup', keywords: 'payroll defaults proration overtime pension scheme' },
  { title: 'Payroll settings · Tax filing & registrations', path: '/payroll/settings?tab=tax-filing', keywords: 'employer registrations state irs paye id tin' },
  { title: 'Payroll settings · GL mapping', path: '/payroll/settings?tab=gl-mapping', keywords: 'gl mapping ledgers payroll accounts health check' },
  { title: 'Payroll setup questionnaire', path: '/payroll/setup', keywords: 'setup wizard questionnaire' },
  { title: 'Team', path: '/team', keywords: 'members invite roles' },
  { title: 'Plan & Billing', path: '/billing', keywords: 'subscription plan card upgrade' },
  { title: 'Settings · General', path: '/settings?tab=general', keywords: 'business profile settings' },
  { title: 'Settings · Banking', path: '/settings?tab=banking', keywords: 'bank settings' },
  { title: 'Settings · Tax', path: '/settings?tab=tax', keywords: 'vat wht tax settings' },
  { title: 'Settings · Payments', path: '/settings?tab=payments', keywords: 'stripe paystack payments' },
  { title: 'Settings · Notifications', path: '/settings?tab=notifications', keywords: 'email push alerts' },
  { title: 'Settings · Security', path: '/settings?tab=security', keywords: 'password 2fa security' },
  { title: 'Settings · AI preferences', path: '/settings?tab=ai-preferences', keywords: 'ai hands free auto post' },
  { title: 'Settings · Activity log', path: '/settings?tab=activity-log', keywords: 'audit activity' },
  { title: 'Settings · Projects', path: '/settings?tab=projects', keywords: 'projects dimensions' },
  { title: 'Audit log', path: '/audit-log', keywords: 'audit trail' },
  { title: 'Notifications', path: '/notifications', keywords: 'alerts inbox' },
  { title: 'Help center', path: '/help', keywords: 'help docs guide how to' },
];

const ICON = { transaction: Banknote, invoice: FileText, bill: Receipt, contact: Users, account: BookOpen, journal: Layers, employee: Briefcase, payroll_run: Wallet, loan: Landmark, fixed_asset: Box, product: Box, other_payment: ReceiptText, other_receipt: ReceiptText, feature: Settings };
const KIND_LABEL = { transaction: 'Transaction', invoice: 'Invoice', bill: 'Bill', contact: 'Contact', account: 'GL account', journal: 'Journal', employee: 'Employee', payroll_run: 'Pay run', loan: 'Loan', fixed_asset: 'Fixed asset', product: 'Product', other_payment: 'Other payment', other_receipt: 'Other receipt', feature: 'Page / feature' };

function matchFeatures(q) {
  const t = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!t.length) return [];
  return FEATURES.filter((f) => { const hay = `${f.title} ${f.keywords}`.toLowerCase(); return t.every((w) => hay.includes(w)); })
    .slice(0, 8).map((f) => ({ kind: 'feature', id: f.path, title: f.title, subtitle: f.path, path: f.path }));
}

export function GlobalSearch({ open, onOpenChange }) {
  const [q, setQ] = useState('');
  const [remote, setRemote] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => { if (!open) { setQ(''); setRemote([]); setActive(0); } }, [open]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setRemote([]); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try { const r = await axios.get(`${API}/search`, { params: { q: q.trim() } }); setRemote(r.data?.data?.results || []); }
      catch { setRemote([]); } finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(timer.current);
  }, [q]);

  const results = useMemo(() => [...matchFeatures(q), ...remote], [q, remote]);
  useEffect(() => { setActive(0); }, [results.length]);

  const go = useCallback((r) => { onOpenChange(false); navigate(r.path); }, [navigate, onOpenChange]);

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter' && results[active]) { e.preventDefault(); go(results[active]); }
  };

  const listRef = useRef(null);
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${active}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const grouped = useMemo(() => {
    const by = {};
    results.forEach((r, i) => { (by[r.kind] = by[r.kind] || []).push({ ...r, idx: i }); });
    return Object.entries(by);
  }, [results]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 sm:max-w-2xl w-[90vw] sm:w-full overflow-hidden flex flex-col top-3 sm:top-[12%] translate-y-0 max-h-[calc(100dvh-1.5rem)] sm:max-h-[76vh] rounded-xl" data-testid="global-search-dialog">
        <DialogTitle className="sr-only">Search</DialogTitle>
        <div className="flex items-center gap-2 border-b border-slate-200 px-3 pr-12 shrink-0 focus-within:border-[#283F8B]/60 transition-colors">
          {loading ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : <Search className="w-4 h-4 text-slate-400" />}
          <input autoFocus autoComplete="off" autoCorrect="off" spellCheck={false} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder="Search anything…"
            style={{ outline: 'none', boxShadow: 'none', WebkitAppearance: 'none' }}
            className="w-full h-12 bg-transparent border-0 !outline-none !ring-0 focus:!outline-none focus:!ring-0 focus-visible:!outline-none focus-visible:!ring-0 text-sm placeholder:text-slate-400" data-testid="global-search-input" />
          <kbd className="hidden sm:inline text-[10px] text-slate-400 border rounded px-1">Esc</kbd>
        </div>
        <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain py-1 pb-[env(safe-area-inset-bottom)]" data-testid="global-search-results">
          {!q.trim() && <p className="px-4 py-6 text-xs text-slate-500">Type to search this business: bank transactions, invoices, bills, contacts, journals, GL accounts, employees, pay runs, loans, assets — and every page or settings tab.</p>}
          {q.trim() && !results.length && !loading && <p className="px-4 py-6 text-sm text-slate-500" data-testid="global-search-empty">No matches for “{q}”.</p>}
          {grouped.map(([kind, rows]) => (
            <div key={kind}>
              <div className="px-6 pt-2.5 pb-1 text-[10px] font-medium uppercase tracking-wider text-slate-400">{KIND_LABEL[kind] || kind}</div>
              {rows.map((r) => { const Icon = ICON[r.kind] || Settings; const on = r.idx === active; return (
                <button key={`${r.kind}-${r.id}`} type="button" data-idx={r.idx} aria-selected={on} tabIndex={-1}
                  onMouseEnter={() => setActive(r.idx)} onClick={() => go(r)} style={{ outline: 'none' }}
                  className={`relative w-[calc(100%-1rem)] mx-2 my-0.5 flex items-center gap-3 pl-4 pr-3 py-2 text-left rounded-lg outline-none focus:outline-none focus-visible:outline-none ring-0 transition-colors duration-100 ${on ? 'bg-[#283F8B]/[0.08] text-[#283F8B]' : 'text-slate-800 hover:bg-slate-100/80'}`}
                  data-testid={`global-search-result-${r.kind}-${r.id}`}>
                  <span aria-hidden className={`absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-[#283F8B] transition-opacity duration-100 ${on ? 'opacity-100' : 'opacity-0'}`} />
                  <Icon className={`w-4 h-4 shrink-0 transition-colors ${on ? 'text-[#283F8B]' : 'text-slate-500'}`} />
                  <span className="flex-1 min-w-0">
                    <span className={`block text-sm truncate ${on ? 'font-medium text-[#283F8B]' : 'text-slate-800'}`}>{r.title}</span>
                    {r.subtitle && <span className={`block text-[11px] truncate ${on ? 'text-[#283F8B]/70' : 'text-slate-500'}`}>{r.subtitle}</span>}
                  </span>
                  <ArrowRight className={`w-3.5 h-3.5 shrink-0 transition-all duration-150 ${on ? 'text-[#283F8B] translate-x-0 opacity-100' : 'text-slate-300 -translate-x-1 opacity-0'}`} />
                </button>); })}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const OPEN_EVENT = 'akf-global-search-open';

/** Mount ONCE per layout: owns the dialog + Ctrl/Cmd+K binding; triggers anywhere just dispatch the open event. */
export function GlobalSearchHost() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const h = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen((o) => !o); } };
    const o = () => setOpen(true);
    window.addEventListener('keydown', h); window.addEventListener(OPEN_EVENT, o);
    return () => { window.removeEventListener('keydown', h); window.removeEventListener(OPEN_EVENT, o); };
  }, []);
  return <GlobalSearch open={open} onOpenChange={setOpen} />;
}

/** Header search icon (desktop sidebar + mobile header). */
export function GlobalSearchTrigger({ className = '', iconClassName = 'w-5 h-5' }) {
  return (
    <button type="button" onClick={() => window.dispatchEvent(new Event(OPEN_EVENT))} title="Search anything (Ctrl/⌘ K)" aria-label="Search"
      className={`p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors ${className}`} data-testid="global-search-trigger">
      <Search className={iconClassName} />
    </button>
  );
}
